import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Right-aligned actions (policy-gated by the caller). */
  actions?: ReactNode;
  /** Content above the title — the page's `Breadcrumb` or the org-detail tabs. */
  breadcrumb?: ReactNode;
  /** `h1` for the page's own title, `h2` when the shell header already owns `h1`. */
  titleAs?: 'h1' | 'h2';
  /** `id` on the title element, for `aria-labelledby` on the page's section. */
  titleId?: string;
  className?: string;
}

/** Title + description + one actions row (design §15.3). One per page. */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
  titleAs = 'h1',
  titleId,
  className,
}: PageHeaderProps) {
  const TitleTag = titleAs;
  return (
    <div className={cn('mb-6 space-y-2', className)}>
      {breadcrumb}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <TitleTag id={titleId} className="text-2xl font-semibold tracking-tight">
            {title}
          </TitleTag>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
